//
//  SDKDTJSONConverter.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
//  Lightweight parser for dimension-like values (number with optional unit). Supports simple arithmetic when a single unit is present (e.g. calc(8px * 2)).
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation
/** Parsing utility */
export class DTExpressionParser {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Expressions
    /** Parsing and reduction utility for simple math expressions */
    static reduceExpressionsToBaseForm(baseExpression) {
        try {
            const expression = baseExpression.trim();
            // Matches optional sign, integer/decimal, optional unit (letters or %), optionally separated by whitespace.
            const match = /^([+-]?(?:\d+(?:\.\d+)?|\.\d+))(?:\s*([a-zA-Z%]+))?$/u.exec(expression);
            if (match) {
                const [, numericPart, unit] = match;
                if (!numericPart) {
                    return baseExpression;
                }
                const value = Number.parseFloat(numericPart);
                if (!Number.isFinite(value)) {
                    return baseExpression;
                }
                if (!unit) {
                    return value;
                }
                const normalizedUnit = unit.toLowerCase();
                return `${numericPart}${normalizedUnit}`;
            }
            return this.evaluateSingleUnitExpression(baseExpression);
        }
        catch (_a) {
            return baseExpression;
        }
    }
    static evaluateSingleUnitExpression(baseExpression) {
        var _a;
        const unitMatches = baseExpression.match(/[a-zA-Z%]+/g) || [];
        const keywords = new Set(["calc", "min", "max", "clamp"]);
        const uniqueUnits = Array.from(new Set(unitMatches.map((u) => u.toLowerCase()).filter((u) => !keywords.has(u))));
        if (uniqueUnits.length > 1) {
            return baseExpression;
        }
        const unit = (_a = uniqueUnits[0]) !== null && _a !== void 0 ? _a : "";
        const unitRegex = unit ? new RegExp(unit, "ig") : null;
        const numericExpression = unitRegex
            ? baseExpression.replace(unitRegex, "")
            : baseExpression;
        const cleaned = numericExpression.replace(/\bcalc\s*\(/gi, "(").trim();
        const value = evaluateMathExpression(cleaned);
        if (value === null || !Number.isFinite(value)) {
            return baseExpression;
        }
        if (!unit) {
            return Number.parseFloat(value.toFixed(3));
        }
        return `${value}${unit}`;
    }
}
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Helpers
class MathExpressionParser {
    constructor(expression) {
        this.index = 0;
        this.source = expression;
    }
    parse() {
        const value = this.parseExpression();
        this.skipWhitespace();
        if (value === null || this.index < this.source.length) {
            return null;
        }
        return value;
    }
    parseExpression() {
        let value = this.parseTerm();
        if (value === null) {
            return null;
        }
        let op = this.peek();
        while (op === "+" || op === "-") {
            this.index += 1;
            const rhs = this.parseTerm();
            if (rhs === null) {
                return null;
            }
            value = op === "+" ? value + rhs : value - rhs;
            op = this.peek();
        }
        return value;
    }
    parseTerm() {
        let value = this.parseFactor();
        if (value === null) {
            return null;
        }
        let op = this.peek();
        while (op === "*" || op === "/") {
            this.index += 1;
            const rhs = this.parseFactor();
            if (rhs === null) {
                return null;
            }
            value = op === "*" ? value * rhs : value / rhs;
            op = this.peek();
        }
        return value;
    }
    parseFactor() {
        this.skipWhitespace();
        let value = this.parseUnary();
        if (value === null) {
            return null;
        }
        this.skipWhitespace();
        while (this.peek() === "^") {
            this.index += 1;
            const rhs = this.parseUnary();
            if (rhs === null) {
                return null;
            }
            value **= rhs;
            this.skipWhitespace();
        }
        return value;
    }
    parseUnary() {
        this.skipWhitespace();
        const op = this.peek();
        if (op === "+" || op === "-") {
            this.index += 1;
            const value = this.parseUnary();
            if (value === null) {
                return null;
            }
            return op === "-" ? -value : value;
        }
        return this.parsePrimary();
    }
    parsePrimary() {
        this.skipWhitespace();
        const char = this.peek();
        if (char === "(") {
            this.index += 1;
            const value = this.parseExpression();
            this.skipWhitespace();
            if (this.peek() !== ")") {
                return null;
            }
            this.index += 1;
            return value;
        }
        if (this.isDigit(char) || char === ".") {
            return this.parseNumber();
        }
        if (this.isAlpha(char)) {
            const identifier = this.parseIdentifier();
            this.skipWhitespace();
            if (this.peek() !== "(") {
                return null;
            }
            this.index += 1;
            const args = [];
            let next = this.peek();
            while (next !== ")" && next !== undefined) {
                const arg = this.parseExpression();
                if (arg === null) {
                    return null;
                }
                args.push(arg);
                this.skipWhitespace();
                if (this.peek() === ",") {
                    this.index += 1;
                    this.skipWhitespace();
                    next = this.peek();
                    continue;
                }
                next = this.peek();
            }
            if (this.peek() !== ")") {
                return null;
            }
            this.index += 1;
            return this.evaluateFunction(identifier, args);
        }
        return null;
    }
    evaluateFunction(name, args) {
        switch (name.toLowerCase()) {
            case "min":
                return args.length ? Math.min(...args) : null;
            case "max":
                return args.length ? Math.max(...args) : null;
            case "clamp": {
                if (args.length !== 3) {
                    return null;
                }
                const [minValue, targetValue, maxValue] = args;
                if (minValue === undefined ||
                    targetValue === undefined ||
                    maxValue === undefined) {
                    return null;
                }
                return Math.min(Math.max(targetValue, minValue), maxValue);
            }
            default:
                return null;
        }
    }
    parseNumber() {
        const start = this.index;
        while (this.isDigit(this.peek()) || this.peek() === ".") {
            this.index += 1;
        }
        const slice = this.source.slice(start, this.index);
        if (!slice) {
            return null;
        }
        const value = Number.parseFloat(slice);
        return Number.isFinite(value) ? value : null;
    }
    skipWhitespace() {
        while (this.peek() === " " ||
            this.peek() === "\t" ||
            this.peek() === "\n") {
            this.index += 1;
        }
    }
    peek() {
        return this.source[this.index];
    }
    isDigit(char) {
        return !!char && char >= "0" && char <= "9";
    }
    isAlpha(char) {
        return !!char && /[a-z]/i.test(char);
    }
    parseIdentifier() {
        const start = this.index;
        while (this.isAlpha(this.peek())) {
            this.index += 1;
        }
        return this.source.slice(start, this.index);
    }
}
function evaluateMathExpression(expression) {
    try {
        const parser = new MathExpressionParser(expression);
        return parser.parse();
    }
    catch (_a) {
        return null;
    }
}
